import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  ExpandLess,
  ExpandMore,
  Image as ImageIcon,
  Notes,
  SpaceBar,
  Title as TitleIcon,
  ContentCut,
} from '@mui/icons-material';
import { Artifact, platformApi } from '../../services/platformApi';
import { CanvasEditor } from './CanvasEditor';
import {
  FixedPageUnit,
  FlowBlock,
  FlowSectionUnit,
  HeadingBlock,
  ImageFlowBlock,
  LayoutUnit,
  PageBreakBlock,
  ParagraphBlock,
  SpacerBlock,
  createFixedPageUnit,
  createFlowSection,
  createHeadingBlock,
  createImageBlock,
  createParagraphBlock,
  isFlowSection,
} from './manuscriptSchema';

type Translate = (key: string) => string;

// Drag payload shared with CanvasEditor's photo tray so the same dragged photo
// can be dropped either onto a fixed page (model B) or into a text flow (model A).
const PHOTO_MIME = 'application/talegence-photo-artifact';

interface FlowEditorProps {
  // A "matter" array of the manuscript (front_matter, content, or back_matter).
  // Editing each matter with the same component keeps front/back matter and the
  // body on one authoring model.
  units: LayoutUnit[];
  onUnitsChange: (next: LayoutUnit[]) => void;
  photoArtifacts: Artifact[];
  projectId?: number;
  textDirection: string;
  pageWidthCm: number;
  pageHeightCm: number;
  disabled?: boolean;
  t: Translate;
}

// Move an item within an array, returning a new array (no-op if out of bounds).
const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= items.length) {
    return items;
  }
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const blockLabel = (block: FlowBlock, t: Translate): string => {
  switch (block.type) {
    case 'heading':
      return t('subHeading');
    case 'paragraph':
      return t('paragraph');
    case 'image':
      return t('image');
    case 'spacer':
      return t('spacer');
    case 'page_break':
      return t('pageBreak');
    default:
      return '';
  }
};

const blockIcon = (block: FlowBlock): React.ReactElement => {
  switch (block.type) {
    case 'heading':
      return <TitleIcon fontSize="small" />;
    case 'image':
      return <ImageIcon fontSize="small" />;
    case 'spacer':
      return <SpaceBar fontSize="small" />;
    case 'page_break':
      return <ContentCut fontSize="small" />;
    default:
      return <Notes fontSize="small" />;
  }
};

export const FlowEditor: React.FC<FlowEditorProps> = ({
  units,
  onUnitsChange,
  photoArtifacts,
  projectId,
  textDirection,
  pageWidthCm,
  pageHeightCm,
  disabled = false,
  t,
}) => {
  const content: LayoutUnit[] = units ?? [];
  const sectionCount = content.filter(isFlowSection).length;
  const fixedCount = content.length - sectionCount;

  // View state: keep the rendered DOM small on real manuscripts (a single
  // chapter can hold hundreds of paragraphs). Sections collapse, and an
  // expanded section only mounts a window of its blocks with a "show more".
  const PAGE_SIZE = 25;
  // Default: expand the first section only when the book is small enough to be
  // comfortable; otherwise start fully collapsed so opening the dialog is fast.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [visible, setVisible] = useState<Record<string, number>>({});
  // Drag-and-drop image placement (model A): a photo dragged from the section
  // tray drops into a gap between blocks, inserting an image block there so it
  // reflows with the surrounding text. `photoDragging` widens every gap while a
  // drag is in flight; `dragOverZone` highlights the gap under the cursor.
  const [photoDragging, setPhotoDragging] = useState(false);
  const [dragOverZone, setDragOverZone] = useState<string | null>(null);

  const imageUrl = (artifactId?: number): string | null =>
    projectId && artifactId ? platformApi.getProjectArtifactDownloadUrl(projectId, artifactId) : null;

  const isExpanded = (id: string) => expanded[id] ?? false;
  const toggleExpanded = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !isExpanded(id) }));
  const visibleCount = (id: string) => visible[id] ?? PAGE_SIZE;
  const showMore = (id: string) => setVisible((prev) => ({ ...prev, [id]: visibleCount(id) + PAGE_SIZE }));

  const commitContent = (nextContent: LayoutUnit[]) => {
    onUnitsChange(nextContent);
  };

  const updateUnit = (index: number, unit: LayoutUnit) => {
    commitContent(content.map((item, idx) => (idx === index ? unit : item)));
  };

  const updateSectionBlocks = (index: number, blocks: FlowBlock[]) => {
    const unit = content[index];
    if (!isFlowSection(unit)) {
      return;
    }
    updateUnit(index, { ...unit, blocks });
  };

  const addSection = () => {
    commitContent([...content, createFlowSection(t('untitledSection'))]);
  };

  const addFixedUnit = (kind: 'page' | 'spread') => {
    const unit = createFixedPageUnit(kind);
    commitContent([...content, unit]);
    setExpanded((prev) => ({ ...prev, [unit.id]: true }));
  };

  const updateFixedUnit = (index: number, unit: FixedPageUnit) => updateUnit(index, unit);

  const removeUnit = (index: number) => {
    commitContent(content.filter((_, idx) => idx !== index));
  };

  const moveUnit = (index: number, direction: -1 | 1) => {
    commitContent(moveItem(content, index, index + direction));
  };

  const addBlock = (sectionIndex: number, block: FlowBlock) => {
    const unit = content[sectionIndex];
    if (!isFlowSection(unit)) {
      return;
    }
    updateSectionBlocks(sectionIndex, [...unit.blocks, block]);
    // Keep the newly appended block visible: expand and widen the window.
    setExpanded((prev) => ({ ...prev, [unit.id]: true }));
    setVisible((prev) => ({ ...prev, [unit.id]: unit.blocks.length + 1 }));
  };

  const insertBlockAt = (sectionIndex: number, blockIndex: number, block: FlowBlock) => {
    const unit = content[sectionIndex];
    if (!isFlowSection(unit)) {
      return;
    }
    const nextBlocks = [...unit.blocks];
    nextBlocks.splice(blockIndex, 0, block);
    updateSectionBlocks(sectionIndex, nextBlocks);
    setExpanded((prev) => ({ ...prev, [unit.id]: true }));
    setVisible((prev) => ({ ...prev, [unit.id]: Math.max(visibleCount(unit.id), nextBlocks.length) }));
  };

  const isPhotoDrag = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.types).includes(PHOTO_MIME);

  const updateBlock = (sectionIndex: number, blockIndex: number, block: FlowBlock) => {
    const unit = content[sectionIndex];
    if (!isFlowSection(unit)) {
      return;
    }
    updateSectionBlocks(
      sectionIndex,
      unit.blocks.map((item, idx) => (idx === blockIndex ? block : item))
    );
  };

  const removeBlock = (sectionIndex: number, blockIndex: number) => {
    const unit = content[sectionIndex];
    if (!isFlowSection(unit)) {
      return;
    }
    updateSectionBlocks(
      sectionIndex,
      unit.blocks.filter((_, idx) => idx !== blockIndex)
    );
  };

  const moveBlock = (sectionIndex: number, blockIndex: number, direction: -1 | 1) => {
    const unit = content[sectionIndex];
    if (!isFlowSection(unit)) {
      return;
    }
    updateSectionBlocks(sectionIndex, moveItem(unit.blocks, blockIndex, blockIndex + direction));
  };

  const renderBlockFields = (sectionIndex: number, blockIndex: number, block: FlowBlock) => {
    if (block.type === 'paragraph') {
      const paragraph = block as ParagraphBlock;
      return (
        <Stack spacing={1}>
          <TextField
            value={paragraph.text}
            onChange={(event) =>
              updateBlock(sectionIndex, blockIndex, { ...paragraph, text: event.target.value })
            }
            placeholder={t('paragraphText')}
            multiline
            minRows={2}
            fullWidth
            disabled={disabled}
            inputProps={{ dir: textDirection }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('paragraphStyle')}</InputLabel>
              <Select
                label={t('paragraphStyle')}
                value={paragraph.style}
                onChange={(event) =>
                  updateBlock(sectionIndex, blockIndex, {
                    ...paragraph,
                    style: event.target.value as ParagraphBlock['style'],
                  })
                }
                disabled={disabled}
              >
                <MenuItem value="body">{t('bodyText')}</MenuItem>
                <MenuItem value="quote">{t('quotedText')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('indentLevel')}</InputLabel>
              <Select
                label={t('indentLevel')}
                value={String(paragraph.indent_level)}
                onChange={(event) =>
                  updateBlock(sectionIndex, blockIndex, {
                    ...paragraph,
                    indent_level: Number(event.target.value),
                  })
                }
                disabled={disabled}
              >
                <MenuItem value="0">0</MenuItem>
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="2">2</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      );
    }

    if (block.type === 'heading') {
      const heading = block as HeadingBlock;
      return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            value={heading.text}
            onChange={(event) =>
              updateBlock(sectionIndex, blockIndex, { ...heading, text: event.target.value })
            }
            placeholder={t('headingText')}
            fullWidth
            sx={{ flex: 1, minWidth: 200 }}
            disabled={disabled}
            inputProps={{ dir: textDirection }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('headingLevel')}</InputLabel>
            <Select
              label={t('headingLevel')}
              value={heading.level}
              onChange={(event) =>
                updateBlock(sectionIndex, blockIndex, {
                  ...heading,
                  level: event.target.value as HeadingBlock['level'],
                })
              }
              disabled={disabled}
            >
              <MenuItem value="sub_chapter">{t('subChapter')}</MenuItem>
              <MenuItem value="sub_sub_chapter">{t('subSubChapter')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      );
    }

    if (block.type === 'image') {
      const image = block as ImageFlowBlock;
      const thumb = imageUrl(image.artifact_id);
      return (
        <Stack spacing={1}>
          {thumb && (
            <Box
              component="img"
              src={thumb}
              alt=""
              draggable={false}
              sx={{
                width: 96,
                height: 72,
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                filter: image.filter === 'grayscale' ? 'grayscale(1)' : image.filter === 'sepia' ? 'sepia(1)' : 'none',
              }}
            />
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }} disabled={disabled || photoArtifacts.length === 0}>
              <InputLabel>{t('photo')}</InputLabel>
              <Select
                label={t('photo')}
                value={image.artifact_id ? String(image.artifact_id) : ''}
                onChange={(event) =>
                  updateBlock(sectionIndex, blockIndex, {
                    ...image,
                    artifact_id: Number(event.target.value),
                  })
                }
              >
                {photoArtifacts.map((artifact) => (
                  <MenuItem key={artifact.id} value={String(artifact.id)}>
                    {artifact.title || `#${artifact.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('imageLayout')}</InputLabel>
              <Select
                label={t('imageLayout')}
                value={image.layout}
                onChange={(event) =>
                  updateBlock(sectionIndex, blockIndex, {
                    ...image,
                    layout: event.target.value as ImageFlowBlock['layout'],
                  })
                }
                disabled={disabled}
              >
                <MenuItem value="inline">{t('layoutInline')}</MenuItem>
                <MenuItem value="float">{t('layoutFloat')}</MenuItem>
                <MenuItem value="full_width">{t('layoutFullWidth')}</MenuItem>
                <MenuItem value="full_page">{t('layoutFullPage')}</MenuItem>
                <MenuItem value="full_bleed">{t('layoutFullBleed')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t('alignment')}</InputLabel>
              <Select
                label={t('alignment')}
                value={image.align}
                onChange={(event) =>
                  updateBlock(sectionIndex, blockIndex, {
                    ...image,
                    align: event.target.value as ImageFlowBlock['align'],
                  })
                }
                disabled={disabled}
              >
                <MenuItem value="left">{t('left')}</MenuItem>
                <MenuItem value="center">{t('center')}</MenuItem>
                <MenuItem value="right">{t('right')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              type="number"
              size="small"
              label={t('widthPercent')}
              value={image.width_percent}
              onChange={(event) =>
                updateBlock(sectionIndex, blockIndex, {
                  ...image,
                  width_percent: Number(event.target.value),
                })
              }
              sx={{ width: 120 }}
              disabled={disabled}
            />
          </Stack>
          <TextField
            size="small"
            label={t('caption')}
            value={image.caption}
            onChange={(event) =>
              updateBlock(sectionIndex, blockIndex, { ...image, caption: event.target.value })
            }
            fullWidth
            disabled={disabled}
            inputProps={{ dir: textDirection }}
          />
        </Stack>
      );
    }

    if (block.type === 'spacer') {
      const spacer = block as SpacerBlock;
      return (
        <TextField
          type="number"
          size="small"
          label={t('spacerHeightCm')}
          value={spacer.height_cm}
          onChange={(event) =>
            updateBlock(sectionIndex, blockIndex, { ...spacer, height_cm: Number(event.target.value) })
          }
          sx={{ width: 160 }}
          disabled={disabled}
          InputProps={{ endAdornment: <InputAdornment position="end">{t('cm')}</InputAdornment> }}
        />
      );
    }

    if (block.type === 'page_break') {
      const pageBreak = block as PageBreakBlock;
      return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('breakToSide')}</InputLabel>
          <Select
            label={t('breakToSide')}
            value={pageBreak.to_side}
            onChange={(event) =>
              updateBlock(sectionIndex, blockIndex, { ...pageBreak, to_side: event.target.value })
            }
            disabled={disabled}
          >
            <MenuItem value="next">{t('sideNext')}</MenuItem>
            <MenuItem value="left">{t('sideLeft')}</MenuItem>
            <MenuItem value="right">{t('sideRight')}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    return null;
  };

  // A draggable strip of the project's photos. Dragging one starts a model-A
  // placement; the block gaps below become drop targets.
  const renderPhotoTray = () => (
    <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', py: 0.5 }}>
      {photoArtifacts.map((artifact) => {
        const thumb = imageUrl(artifact.id);
        return (
          <Tooltip key={artifact.id} title={t('dragImageToPlace')}>
            <Box
              draggable={!disabled}
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = 'copy';
                event.dataTransfer.setData(PHOTO_MIME, String(artifact.id));
                setPhotoDragging(true);
              }}
              onDragEnd={() => {
                setPhotoDragging(false);
                setDragOverZone(null);
              }}
              sx={{
                flex: '0 0 auto',
                width: 44,
                height: 44,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                cursor: disabled ? 'default' : 'grab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
              }}
            >
              {thumb ? (
                <Box component="img" src={thumb} alt="" draggable={false} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <ImageIcon fontSize="small" color="disabled" />
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );

  // A gap between blocks that accepts a dragged photo, inserting an image block
  // at `blockIndex` on drop.
  const renderDropZone = (sectionIndex: number, blockIndex: number) => {
    const key = `${sectionIndex}-${blockIndex}`;
    const active = dragOverZone === key;
    if (!photoDragging && !active) {
      return null;
    }
    return (
      <Box
        key={`drop-${key}`}
        onDragOver={(event) => {
          if (disabled || !isPhotoDrag(event)) {
            return;
          }
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          setDragOverZone(key);
        }}
        onDragLeave={() => setDragOverZone((prev) => (prev === key ? null : prev))}
        onDrop={(event) => {
          if (disabled || !isPhotoDrag(event)) {
            return;
          }
          event.preventDefault();
          const artifactId = Number(event.dataTransfer.getData(PHOTO_MIME));
          setDragOverZone(null);
          setPhotoDragging(false);
          if (artifactId > 0) {
            insertBlockAt(sectionIndex, blockIndex, createImageBlock(artifactId));
          }
        }}
        sx={{
          height: active ? 30 : 16,
          borderRadius: 1,
          border: '2px dashed',
          borderColor: active ? 'primary.main' : 'divider',
          bgcolor: active ? 'action.selected' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 120ms',
        }}
      >
        {active && (
          <Typography variant="caption" color="primary">
            {t('dropImageHere')}
          </Typography>
        )}
      </Box>
    );
  };

  const renderSection = (unit: FlowSectionUnit, index: number) => (
    <Paper key={unit.id} variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            label={t('sectionTitle')}
            value={unit.title}
            onChange={(event) => updateUnit(index, { ...unit, title: event.target.value })}
            sx={{ flex: 1, minWidth: 220 }}
            disabled={disabled}
            inputProps={{ dir: textDirection }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('titleLevel')}</InputLabel>
            <Select
              label={t('titleLevel')}
              value={unit.title_level}
              onChange={(event) =>
                updateUnit(index, { ...unit, title_level: event.target.value as FlowSectionUnit['title_level'] })
              }
              disabled={disabled}
            >
              <MenuItem value="chapter">{t('chapter')}</MenuItem>
              <MenuItem value="sub_chapter">{t('subChapter')}</MenuItem>
              <MenuItem value="sub_sub_chapter">{t('subSubChapter')}</MenuItem>
              <MenuItem value="none">{t('none')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('startsOn')}</InputLabel>
            <Select
              label={t('startsOn')}
              value={unit.start_side}
              onChange={(event) =>
                updateUnit(index, { ...unit, start_side: event.target.value as FlowSectionUnit['start_side'] })
              }
              disabled={disabled}
            >
              <MenuItem value="next">{t('sideNext')}</MenuItem>
              <MenuItem value="left">{t('sideLeft')}</MenuItem>
              <MenuItem value="right">{t('sideRight')}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Chip size="small" label={`${unit.blocks.length} ${t('blocks')}`} variant="outlined" />
          <Tooltip title={isExpanded(unit.id) ? t('collapse') : t('expand')}>
            <IconButton size="small" onClick={() => toggleExpanded(unit.id)}>
              {isExpanded(unit.id) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('moveUp')}>
            <span>
              <IconButton size="small" onClick={() => moveUnit(index, -1)} disabled={disabled || index === 0}>
                <ArrowUpward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('moveDown')}>
            <span>
              <IconButton
                size="small"
                onClick={() => moveUnit(index, 1)}
                disabled={disabled || index === content.length - 1}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('removeSection')}>
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => removeUnit(index)}
                disabled={disabled}
              >
                <Delete fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {isExpanded(unit.id) && (
          <>
            <Divider />

            {photoArtifacts.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('dragImagesHint')}
                </Typography>
                {renderPhotoTray()}
              </Stack>
            )}

            {unit.blocks.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('sectionEmpty')}
              </Typography>
            )}

            {unit.blocks.slice(0, visibleCount(unit.id)).map((block, blockIndex) => (
              <React.Fragment key={`${unit.id}-block-${blockIndex}`}>
                {renderDropZone(index, blockIndex)}
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Chip size="small" icon={blockIcon(block)} label={blockLabel(block, t)} variant="outlined" />
                    <Box sx={{ flexGrow: 1 }}>{renderBlockFields(index, blockIndex, block)}</Box>
                    <Stack>
                      <IconButton
                        size="small"
                        onClick={() => moveBlock(index, blockIndex, -1)}
                        disabled={disabled || blockIndex === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveBlock(index, blockIndex, 1)}
                        disabled={disabled || blockIndex === unit.blocks.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => removeBlock(index, blockIndex)} disabled={disabled}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              </React.Fragment>
            ))}

            {renderDropZone(index, Math.min(unit.blocks.length, visibleCount(unit.id)))}

            {unit.blocks.length > visibleCount(unit.id) && (
              <Button size="small" onClick={() => showMore(unit.id)}>
                {`${t('showMore')} (${unit.blocks.length - visibleCount(unit.id)})`}
              </Button>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" startIcon={<Notes />} onClick={() => addBlock(index, createParagraphBlock())} disabled={disabled}>
                {t('paragraph')}
              </Button>
              <Button size="small" startIcon={<TitleIcon />} onClick={() => addBlock(index, createHeadingBlock())} disabled={disabled}>
                {t('subHeading')}
              </Button>
              <Button
                size="small"
                startIcon={<ImageIcon />}
                onClick={() => addBlock(index, createImageBlock(photoArtifacts[0]?.id ?? 0))}
                disabled={disabled || photoArtifacts.length === 0}
              >
                {t('image')}
              </Button>
              <Button
                size="small"
                startIcon={<SpaceBar />}
                onClick={() => addBlock(index, { type: 'spacer', height_cm: 1 })}
                disabled={disabled}
              >
                {t('spacer')}
              </Button>
              <Button
                size="small"
                startIcon={<ContentCut />}
                onClick={() => addBlock(index, { type: 'page_break', to_side: 'next' })}
                disabled={disabled}
              >
                {t('pageBreak')}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );

  const renderFixedUnit = (unit: FixedPageUnit, index: number) => (
    <Paper key={unit.id} variant="outlined" sx={{ p: 2, borderStyle: 'dashed' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            icon={<ImageIcon fontSize="small" />}
            label={t(unit.kind === 'spread' ? 'fixedSpread' : 'fixedPage')}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('pageRole')}</InputLabel>
            <Select
              label={t('pageRole')}
              value={unit.page_role}
              onChange={(event) => updateFixedUnit(index, { ...unit, page_role: event.target.value })}
              disabled={disabled}
            >
              <MenuItem value="content">{t('roleContent')}</MenuItem>
              <MenuItem value="full_bleed_image">{t('roleFullBleedImage')}</MenuItem>
              <MenuItem value="title">{t('roleTitle')}</MenuItem>
              <MenuItem value="part_divider">{t('rolePartDivider')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('startsOn')}</InputLabel>
            <Select
              label={t('startsOn')}
              value={unit.start_side}
              onChange={(event) =>
                updateFixedUnit(index, { ...unit, start_side: event.target.value as FixedPageUnit['start_side'] })
              }
              disabled={disabled}
            >
              <MenuItem value="next">{t('sideNext')}</MenuItem>
              <MenuItem value="left">{t('sideLeft')}</MenuItem>
              <MenuItem value="right">{t('sideRight')}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Chip size="small" label={`${unit.blocks.length} ${t('blocks')}`} variant="outlined" />
          <Tooltip title={isExpanded(unit.id) ? t('collapse') : t('expand')}>
            <IconButton size="small" onClick={() => toggleExpanded(unit.id)}>
              {isExpanded(unit.id) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('moveUp')}>
            <span>
              <IconButton size="small" onClick={() => moveUnit(index, -1)} disabled={disabled || index === 0}>
                <ArrowUpward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('moveDown')}>
            <span>
              <IconButton size="small" onClick={() => moveUnit(index, 1)} disabled={disabled || index === content.length - 1}>
                <ArrowDownward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('removeSection')}>
            <span>
              <IconButton size="small" color="error" onClick={() => removeUnit(index)} disabled={disabled}>
                <Delete fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {isExpanded(unit.id) && (
          <>
            <Divider />
            <CanvasEditor
              value={unit}
              onChange={(next) => updateFixedUnit(index, next)}
              photoArtifacts={photoArtifacts}
              projectId={projectId}
              pageWidthCm={pageWidthCm}
              pageHeightCm={pageHeightCm}
              textDirection={textDirection}
              disabled={disabled}
              t={t}
            />
          </>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`${sectionCount} ${t('sections')}`} variant="outlined" />
        {fixedCount > 0 && <Chip size="small" label={`${fixedCount} ${t('fixedPages')}`} variant="outlined" />}
      </Stack>

      {content.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('noSectionsYet')}
        </Typography>
      )}

      {content.map((unit, index) =>
        isFlowSection(unit) ? renderSection(unit, index) : renderFixedUnit(unit, index)
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" startIcon={<Add />} onClick={addSection} disabled={disabled}>
          {t('addSection')}
        </Button>
        <Button variant="outlined" startIcon={<Add />} onClick={() => addFixedUnit('page')} disabled={disabled}>
          {t('addPage')}
        </Button>
        <Button variant="outlined" startIcon={<Add />} onClick={() => addFixedUnit('spread')} disabled={disabled}>
          {t('addSpread')}
        </Button>
      </Stack>
    </Stack>
  );
};
